import { useCallback } from 'react';
import { useEventLocale } from '@shared/core/localization/context/LocaleContext';
import { config } from '@static/js/env.config';
import queryString from 'query-string';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export interface QueryRedirectURL
  extends Readonly<{
    url: string;
    provider?: string;
    guests?: string;
    platform?: string;
    eventId?: string;
    userId?: string;
    l?: string;
  }> {}

/**
 * Format a URL to be passed through the backend redirect
 * @param query
 * @returns
 */
export const formatRedirectURL = (query: Partial<QueryRedirectURL>): string => {
  if (!shouldWrapWithRedirectService(query.url) || !config.redirectServiceUri) {
    return query.url ?? '';
  }
  return queryString.stringifyUrl({
    url: `${config.redirectServiceUri}/redirect`,
    query: query,
  });
};

/**
 * Append `skipRedirectWrapping=true` to a target URL so app-redirect passes it
 * through without the affiliate/monetization chain.
 *
 * app-redirect reads the flag from the target URL's own query string (see
 * `deriveAffinityOptions`), so it must live on the destination URL, not the
 * outer redirect request. The param is an app-redirect directive; affiliate
 * providers don't forward it, and it is stripped by the same URL hygiene that
 * removes Joy's other tracking params downstream.
 */
export const withSkipRedirectWrapping = (url: string): string =>
  queryString.stringifyUrl({
    url,
    query: { skipRedirectWrapping: 'true' },
  });

/**
 * Checks whether the given URL is external and should be wrapped with the redirect service.
 * URLs pointing to `withjoy.com` or its subdomains are considered internal.
 *
 * @param {string} url - The URL to evaluate.
 * @returns {boolean} - `true` if the URL is external; `false` otherwise.
 */
const shouldWrapWithRedirectService = (url?: string): boolean => {
  if (!url) return true;
  try {
    const { hostname } = new URL(url);
    const isInternal = hostname === 'withjoy.com' || hostname.endsWith('.withjoy.com');
    return !isInternal;
  } catch {
    return true;
  }
};

/**
 * Per-call overrides. Deliberately a SECOND argument rather than a field on
 * `QueryRedirectURL`: every key of that object is stringified into the redirect
 * service's own query string, so a directive added there would leak into the
 * URL the guest is sent to.
 */
type FormatRedirectURLOptions = Readonly<{
  /**
   * Keep the affiliate hop even on an event that has opted out of it.
   *
   * Only paid placements pass this. A sponsored card is inventory we were paid
   * to show, and the affiliate hop is what carries the Partnerize camref that
   * attributes the click — skip it and the placement is unattributable, so the
   * advertiser cannot be billed for a click they bought.
   *
   * The tradeoff is real and points the other way for the guest: the opt-out
   * exists because some couples' guests have the affiliate chain blocked by ad
   * blockers or DNS filters, and those guests now get that chain back on
   * sponsored cards specifically. Organic results, room blocks and registry
   * links on the same event keep the opt-out.
   */
  forceAffiliateWrapping?: boolean;
}>;

/**
 * Hook that returns a memoized redirect-URL builder with the event locale
 * (`?l=`) baked in. All React call-sites should use this instead of
 * calling `formatRedirectURL` directly.
 *
 * When the event has the `skipAffiliateRedirectWrapping` feature flag enabled
 * (Service Center), external links are still routed through the redirect
 * service but the destination URL carries `skipRedirectWrapping=true`, which
 * tells app-redirect to skip the affiliate/monetization hop and send the guest
 * straight to the destination. Used for couples whose guests hit the affiliate
 * tracker chain being blocked by ad blockers / DNS filters.
 *
 * `forceAffiliateWrapping` opts a single call back OUT of that opt-out; see
 * `FormatRedirectURLOptions`.
 */
export const useFormatRedirectURL = () => {
  const { language } = useEventLocale();
  const [skipAffiliateWrapping] = useIsFeatureEnabled({
    eventFeatureFlagKey: 'skipAffiliateRedirectWrapping',
  });

  return useCallback(
    (query: Partial<QueryRedirectURL> = {}, options?: FormatRedirectURLOptions) => {
      const shouldSkipWrapping = skipAffiliateWrapping && !options?.forceAffiliateWrapping && query.url && shouldWrapWithRedirectService(query.url);
      return formatRedirectURL({
        url: shouldSkipWrapping ? withSkipRedirectWrapping(query.url as string) : query.url,
        provider: query.provider,
        guests: query.guests,
        platform: query.platform,
        eventId: query.eventId,
        userId: query.userId,
        l: language,
      });
    },
    [language, skipAffiliateWrapping],
  );
};
